/**
 * URL state — single source of truth for how filter state is encoded
 * into the URL query string.
 *
 * Priority (enforced by callers):
 *   1. URL params override everything
 *   2. localStorage fallback
 *   3. default values fallback
 *
 * Design rules:
 *   - Every param key is explicitly allowlisted. Unknown keys are ignored.
 *   - Every value passes a validator before being surfaced. Malformed
 *     input falls through to the next priority tier.
 *   - writeUrlState preserves the current pathname and any non-tracked
 *     query params already present in the URL.
 *   - No-op when the next query string equals the current one, so the
 *     state→URL sync effect never thrashes history.
 */

export const URL_KEY_LOCATION = 'loc';
export const URL_KEY_PERIOD = 'p';
export const URL_KEY_MANAGER = 'mgr';

// Global Operational Context — granular period anchoring + KPI focus.
// These keys persist across major workspaces (Today, Executive, Alerts,
// Requests, Scorecard, Goal Plan, Goals, People & Talent) so an
// operator's mental model of "what am I looking at" survives navigation.
export const URL_KEY_PERIOD_TYPE = 'pt';
export const URL_KEY_YEAR = 'yr';
export const URL_KEY_MONTH = 'mo';
export const URL_KEY_QUARTER = 'qt';
export const URL_KEY_WEEK = 'wk';
export const URL_KEY_KPI = 'kpi';

/** Keys that persist across route changes (global context). */
export const STICKY_KEYS = [
  URL_KEY_LOCATION,
  URL_KEY_PERIOD,
  URL_KEY_PERIOD_TYPE,
  URL_KEY_YEAR,
  URL_KEY_MONTH,
  URL_KEY_QUARTER,
  URL_KEY_WEEK,
  URL_KEY_KPI
];

/** Keys that reset between routes (per-view context). */
export const PER_VIEW_KEYS = [URL_KEY_MANAGER];

export const ALL_TRACKED_KEYS = [...STICKY_KEYS, ...PER_VIEW_KEYS];

export const VALID_PERIODS = ['weekly', 'monthly', 'quarterly', 'yearly'];
export const VALID_PERIOD_TYPES = ['weekly', 'monthly', 'quarterly', 'yearly'];

// Location codes in this app are short alphanumerics (e.g. 'LAX', 'MCO').
// Also accept 'ALL' as the sentinel for all-locations scope.
const LOCATION_CODE_PATTERN = /^[A-Za-z0-9_-]{1,16}$/;

// Manager IDs are numeric strings or the director-location-total sentinel.
const MANAGER_ID_PATTERN = /^[A-Za-z0-9:_-]{1,96}$/;

function isValidLocationCode(code) {
  if (typeof code !== 'string') return false;
  return LOCATION_CODE_PATTERN.test(code);
}

function isValidPeriod(period) {
  return typeof period === 'string' && VALID_PERIODS.includes(period);
}

function isValidManagerId(id) {
  if (typeof id !== 'string') return false;
  return MANAGER_ID_PATTERN.test(id);
}

function isValidPeriodType(value) {
  return typeof value === 'string' && VALID_PERIOD_TYPES.includes(value);
}

function isValidYear(value) {
  if (typeof value !== 'string' || !/^\d{4}$/.test(value)) return false;
  const n = Number(value);
  return n >= 2000 && n <= 2100;
}

function isValidMonth(value) {
  if (typeof value !== 'string' || !/^\d{1,2}$/.test(value)) return false;
  const n = Number(value);
  return n >= 1 && n <= 12;
}

function isValidQuarter(value) {
  if (typeof value !== 'string' || !/^[1-4]$/.test(value)) return false;
  return true;
}

function isValidWeek(value) {
  if (typeof value !== 'string' || !/^\d{1,2}$/.test(value)) return false;
  const n = Number(value);
  return n >= 1 && n <= 53;
}

// KPI keys in this app are short alphanumerics + underscores/dashes.
const KPI_KEY_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
function isValidKpiKey(value) {
  return typeof value === 'string' && KPI_KEY_PATTERN.test(value);
}

const VALIDATORS = {
  [URL_KEY_LOCATION]: isValidLocationCode,
  [URL_KEY_PERIOD]: isValidPeriod,
  [URL_KEY_MANAGER]: isValidManagerId,
  [URL_KEY_PERIOD_TYPE]: isValidPeriodType,
  [URL_KEY_YEAR]: isValidYear,
  [URL_KEY_MONTH]: isValidMonth,
  [URL_KEY_QUARTER]: isValidQuarter,
  [URL_KEY_WEEK]: isValidWeek,
  [URL_KEY_KPI]: isValidKpiKey
};

/**
 * Read and validate URL state from a search string.
 * Accepts an explicit search string for testing; defaults to
 * window.location.search in the browser.
 *
 * @param {string} [search]
 * @returns {{ loc?: string, p?: string, mgr?: string }}
 */
export function readUrlState(search) {
  const raw = typeof search === 'string'
    ? search
    : (typeof window !== 'undefined' ? window.location.search : '');
  if (!raw) return {};

  let params;
  try {
    params = new URLSearchParams(raw);
  } catch {
    return {};
  }

  const out = {};
  for (const key of ALL_TRACKED_KEYS) {
    if (!params.has(key)) continue;
    const value = params.get(key);
    const validator = VALIDATORS[key];
    if (validator && validator(value)) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Build the next search string by merging the provided state into the
 * current search, respecting the allowlist. Values of null/undefined/''
 * remove the key from the URL. Returns the raw query string (no leading
 * '?'); empty string if no params remain.
 *
 * @param {Record<string,string|null|undefined>} nextState
 * @param {string} [currentSearch]
 */
export function buildSearchString(nextState, currentSearch) {
  const raw = typeof currentSearch === 'string'
    ? currentSearch
    : (typeof window !== 'undefined' ? window.location.search : '');
  const params = new URLSearchParams(raw || '');

  for (const [key, value] of Object.entries(nextState || {})) {
    if (!ALL_TRACKED_KEYS.includes(key)) continue;
    if (value == null || value === '') {
      params.delete(key);
      continue;
    }
    const validator = VALIDATORS[key];
    if (validator && !validator(value)) {
      params.delete(key);
      continue;
    }
    params.set(key, String(value));
  }
  return params.toString();
}

/**
 * Write the merged state to window.history. No-op when the resulting
 * URL is identical to the current one (avoids history thrash).
 *
 * @param {Record<string,string|null|undefined>} nextState
 * @param {{ mode?: 'replace' | 'push' }} [options]
 */
export function writeUrlState(nextState, { mode = 'replace' } = {}) {
  if (typeof window === 'undefined' || !window.history) return;
  const nextQuery = buildSearchString(nextState, window.location.search);
  const currentQuery = (window.location.search || '').replace(/^\?/, '');
  if (nextQuery === currentQuery) return;

  const nextUrl = nextQuery
    ? `${window.location.pathname}?${nextQuery}${window.location.hash || ''}`
    : `${window.location.pathname}${window.location.hash || ''}`;

  if (mode === 'push') {
    window.history.pushState({}, '', nextUrl);
  } else {
    window.history.replaceState({}, '', nextUrl);
  }
}

/**
 * Build a path+query string for navigation that preserves the given
 * sticky state. Used by handleNavigate so link clicks carry the
 * current location/period across pages.
 *
 * @param {string} path
 * @param {Record<string,string|null|undefined>} stickyState
 */
export function buildPathWithState(path, stickyState = {}) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(stickyState)) {
    if (!STICKY_KEYS.includes(key)) continue;
    if (value == null || value === '') continue;
    const validator = VALIDATORS[key];
    if (validator && !validator(value)) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}
